import React, { useEffect } from "react";
import CustomTable from "app/components/Table";
import { TableHeaderColumnType } from "app/components/Table/types";
import Uploader from "../Uploader";
import { Checkbox, IconButton } from "@mui/material";
import EditableColumn from "../../../../../../../../components/EditableColumn";
import { styled } from "@mui/material/styles";
import { ASSET_TYPE, MODES } from "utils/constants";
import { useDispatch, useSelector } from "react-redux";
import { selectAnswer } from "app/containers/Questions/redux/selector";
import { actions } from "app/containers/Questions/redux/slice";
import CustomTableRow from "app/components/TableRow";
import { selectDirection } from "styles/theme/slice";
import DeleteForeverTwoToneIcon from "@mui/icons-material/DeleteForeverTwoTone";
import { SingleMultiData as Data } from "./constants";
import {
  MultiAnswerTypes,
  SingleAnswerTypes,
} from "app/containers/Questions/types";
import useDebounceEffect from "utils/hooks/useDebounceEffect";
import PrimaryButton from "app/components/Buttons/PrimaryButton";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
interface StyledCellProps {
  minWidth?: string | number;
  maxWidth?: string | number;
  fontFamily?: string;
}

const StyledTableCell = styled("div")(
  ({ minWidth, maxWidth, fontFamily }: StyledCellProps) => ({
    flex: 1,
    display: "flex",
    alignItems: "center",
    padding: "14px 16px",
    "&:last-of-type": {
      borderRight: "none",
    },
    backgroundColor: "transparent",
    textAlign: "unset",
    whiteSpace: "nowrap",
    height: 50,
    minWidth: minWidth || "auto",
    maxWidth: maxWidth || "none",
    fontFamily,
    fontSize: "14px",
    lineHeight: "16px",
    color: "#3C3C43",
  })
);

const Headers: TableHeaderColumnType[] = [
  {
    id: "#",
    label: "HASH",
    align: "left",
    minWidth: 80,
    maxWidth: 80,
    sortable: false,
  },
  {
    id: "rightAnswer",
    label: "RIGHT_ANSWER",
    align: "left",
    minWidth: 180,
    maxWidth: 180,
    sortable: false,
  },
  {
    id: "text",
    label: "ANSWER_TEXT",
    align: "left",
    minWidth: 500,
    maxWidth: 500,
    sortable: false,
  },
  {
    id: "action",
    label: "ACTIONS",
    align: "left",
    minWidth: 500,
    maxWidth: 500,
    sortable: false,
  },
];
const MAX_ANSWER = 7;
const MultiSelect = ({
  disabled,
  mode,
}: {
  disabled?: boolean;
  mode: MODES;
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const direction = useSelector(selectDirection);
  const answer = useSelector(selectAnswer);
  const value: MultiAnswerTypes =
    (answer.multi_select?.[0]?.answer as MultiAnswerTypes) ||
    "TEXT_WITH_IMAGES";
  const hasImages = (
    ["ONLY_IMAGES", "TEXT_WITH_IMAGES"] as MultiAnswerTypes[]
  ).includes(value);
  const hasText = (
    ["ONLY_TEXT", "TEXT_WITH_IMAGES"] as MultiAnswerTypes[]
  ).includes(value);
  const maxNotReached = (answer.multi_select?.length ?? 0) < MAX_ANSWER;
  const shownHeaders = Headers.filter((header) => {
    if (header.id === "text") {
      return hasText;
    }
    // Include other columns by default
    return true;
  });

  useDebounceEffect(
    () => {
      if (!answer?.multi_select?.length) {
        dispatch(actions.setMultiSelectAnswer(Data));
        dispatch(actions.setMultiSelectAnswer(Data));
        return;
      }

      return () => {};
    },
    [answer?.multi_select, hasImages, hasText],
    100
  );

  const handleAddAnswer = () => {
    if (maxNotReached) {
      dispatch(actions.setMultiSelectAnswer(Data));
      return;
    }
  };

  const disabledDelete = (answer?.multi_select?.length || 0) <= 2 || disabled;

  return (
    <>
      <CustomTable headers={shownHeaders}>
        {answer.multi_select?.map((item, index) => (
          <CustomTableRow key={index}>
            <StyledTableCell minWidth={80} maxWidth={80}>
              {index + 1}
            </StyledTableCell>
            <StyledTableCell minWidth={180} maxWidth={180}>
              <Checkbox
                disabled={disabled}
                checked={item.isCorrect}
                defaultChecked={item.isCorrect}
                onChange={() => {
                  dispatch(
                    actions.setUpdateAnswer({
                      index: index,
                      type: "multi_select",
                      key: "isCorrect",
                      value: !item.isCorrect,
                    })
                  );
                }}
                sx={{
                  "& .MuiSvgIcon-root": {
                    fontSize: "20px",
                    color: "#207DB9",
                  },
                  "&.Mui-disabled .MuiSvgIcon-root": {
                    color: "#8890A6",
                  },
                }}
              />
            </StyledTableCell>
            {hasText && (
              <StyledTableCell minWidth={500} maxWidth={500}>
                <EditableColumn
                  disabled={disabled}
                  value={item.title || ""}
                  isOdd={index % 2 === 0}
                  innerContainerStyle={{
                    maxWidth: "100%",
                  }}
                  inputStyle={{
                    background: "transparent",
                  }}
                  onSave={(val) => {
                    dispatch(
                      actions.setUpdateAnswer({
                        index: index,
                        type: "multi_select",
                        key: "title",
                        value: val,
                      })
                    );
                  }}
                />
              </StyledTableCell>
            )}

            <StyledTableCell minWidth={500} maxWidth={500}>
              {hasImages && (
                <Uploader
                  disabled={disabled}
                  isOdd={!(index % 2 === 0)}
                  onUpload={(img) => {
                    dispatch(
                      actions.setUpdateAnswer({
                        index: index,
                        key: "asset",
                        type: "multi_select",
                        value: {
                          _id: img?._id,
                          type: ASSET_TYPE.IMAGE,
                          mimeType: img?.mimeType,
                          fileName: img?.fileName,
                          filePath: img?.filePath,
                          url: img?.url,
                        },
                      })
                    );
                  }}
                  isDelete={
                    item.asset?.fileName
                      ? item.asset?.fileName.length > 0
                      : false
                  }
                  src={item.asset?.url}
                  onDelete={() => {
                    dispatch(
                      actions.setUpdateAnswer({
                        index: index,
                        key: "asset",
                        type: "multi_select",
                        value: {
                          _id: "",
                          type: ASSET_TYPE.IMAGE,
                          mimeType: "",
                          fileName: "",
                          filePath: "",
                          url: "",
                        },
                      })
                    );
                  }}
                />
              )}
              <IconButton
                sx={{
                  width: "40px",
                  height: "24px",
                  borderRadius: "4px",
                }}
                disabled={disabledDelete}
                onClick={() => {
                  dispatch(
                    actions.setDeleteAnswer({
                      index: index,
                      type: "multi_select",
                    })
                  );
                }}
              >
                <DeleteForeverTwoToneIcon
                  sx={{
                    fontSize: "16px",
                    color: disabledDelete ? "#ccc" : "#E24399",
                  }}
                />
              </IconButton>
            </StyledTableCell>
          </CustomTableRow>
        ))}
        <CustomTableRow key={"add"} style={{ backgroundColor: "#fff" }}>
          <StyledTableCell maxWidth={80} minWidth={80}>
            &nbsp;
          </StyledTableCell>
          <StyledTableCell maxWidth={180} minWidth={180}>
            &nbsp;
          </StyledTableCell>
          {hasText && (
            <StyledTableCell maxWidth={500} minWidth={500}>
              &nbsp;
            </StyledTableCell>
          )}
          <StyledTableCell maxWidth={500} minWidth={500}>
            <PrimaryButton
              sx={{ width: 145 }}
              startIcon={<AddIcon />}
              disabled={!maxNotReached}
              onClick={handleAddAnswer}
              label={t("COMMON.BUTTONS.ADD_NEW_ANSWER")}
            />
          </StyledTableCell>
        </CustomTableRow>
      </CustomTable>
    </>
  );
};

export default React.memo(MultiSelect);
